import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ExternalLink, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { errorCode } from "@/lib/api-errors";
import { formatDateTime } from "@/lib/utils";
import type {
  BillingCustomerEmailResponse,
  InstancePartner,
  InstanceSubscription,
  LinkStripeCustomerRequest,
  LinkStripeCustomerResponse,
  PartnerAttribution,
  SetPartnerResponse,
  VatCheck,
  VatCheckResponse,
  VatConfirmation,
} from "@/types/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StripeCustomerEmailKV } from "./stripe-customer-email-kv";
import { invalidateBilling } from "./use-billing-ops";

const ERROR_CODES = [
  "CUSTOMER_NOT_FOUND",
  "CUSTOMER_LINKED_ELSEWHERE",
  "CUSTOMER_ALREADY_LINKED",
  "SUBSCRIPTION_LIVE",
  "CHECKOUT_IN_PROGRESS",
  "NO_STRIPE_CUSTOMER",
  "NO_BILLING_CONFIG",
  "STRIPE_NOT_CONFIGURED",
  "STRIPE_REJECTED",
  "INSTANCE_NOT_FOUND",
  "VAT_REVIEW_REQUIRED",
] as const;
const isErrorCode = (c: string | null): c is (typeof ERROR_CODES)[number] =>
  c !== null && (ERROR_CODES as readonly string[]).includes(c);

const WARNING_CODES = [
  "VAT_REVIEW_NO_COUNTRY",
  "VAT_REVIEW_UNVERIFIED_VAT_ID",
  "VAT_REVIEW_BZST_INVALID",
  "VAT_REVIEW_BZST_MISMATCH",
  "VAT_REVIEW_BZST_UNAVAILABLE",
  "VAT_REVIEW_BZST_ERROR",
  "VAT_REVIEW_BZST_SIMPLE_ONLY",
  "VAT_REVIEW_BZST_PARTIAL",
  "VAT_REVIEW_BZST_SPECIAL_CASE",
  "VAT_REVIEW_BZST_NO_COMPANY",
  "VAT_REVIEW_NON_EU_BUSINESS_UNCONFIRMED",
  "NOT_APPLIED_SUBSCRIPTION_LIVE",
  "LEGAL_NOTE_NOT_WRITTEN",
  "CUSTOMER_TAGGED_FOR_OTHER_INSTANCE",
] as const;
const isWarningCode = (c: string): c is (typeof WARNING_CODES)[number] =>
  (WARNING_CODES as readonly string[]).includes(c);

function useErrorToast() {
  const { t } = useTranslation("billing");
  return (err: unknown) => {
    const code = errorCode(err);
    toast.error(
      isErrorCode(code)
        ? t(`stripeCustomer.errors.${code}`)
        : err instanceof Error
          ? err.message
          : t("stripeCustomer.errors.generic"),
      { description: code === "STRIPE_REJECTED" && err instanceof Error ? err.message : undefined }
    );
  };
}

interface Props {
  name: string;
  subscription: InstanceSubscription | null;
  vatCheck: VatCheck | null;
  vatConfirmations: VatConfirmation[];
  partner: InstancePartner | null;
}

/**
 * «Клиент Stripe»: the instance's Stripe customer — link an existing one,
 * check VAT (our rules + BZSt), record the Affonso partner.
 * Backend: /api/admin/instances/:name/billing/{customer,link-stripe-customer,vat-check,partner}.
 */
export function StripeCustomerCard({ name, subscription, vatCheck, vatConfirmations, partner }: Props) {
  const { t } = useTranslation("billing");
  const customerId = subscription?.stripeCustomerId ?? null;
  const [showLink, setShowLink] = React.useState(false);

  // Same query (and cache) as StripeCustomerEmailKV; it knows test vs live.
  const { data: customerInfo } = useQuery({
    queryKey: ["instance", name, "billing", "customer"],
    queryFn: () =>
      api.get<BillingCustomerEmailResponse>(`/api/admin/instances/${name}/billing/customer`),
    enabled: Boolean(customerId),
    retry: false,
  });
  const dashboardUrl = customerId
    ? (customerInfo?.dashboardUrl ?? `https://dashboard.stripe.com/customers/${customerId}`)
    : null;
  const lastConfirmation = vatConfirmations.length ? vatConfirmations[vatConfirmations.length - 1] : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("stripeCustomer.title")}</CardTitle>
        <CardDescription>{t("stripeCustomer.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {customerId ? (
          <dl className="space-y-1 text-sm">
            <div className="flex gap-2">
              <dt className="w-28 text-muted-foreground">{t("stripeCustomer.id")}</dt>
              <dd className="flex flex-wrap items-center gap-2 font-mono text-xs">
                {customerId}
                {dashboardUrl ? (
                  <a
                    href={dashboardUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 font-sans text-primary hover:underline"
                  >
                    {t("stripeCustomer.openInStripe")} <ExternalLink className="h-3 w-3" />
                  </a>
                ) : null}
              </dd>
            </div>
            <StripeCustomerEmailKV name={name} />
          </dl>
        ) : (
          <p className="text-sm text-muted-foreground">{t("stripeCustomer.none")}</p>
        )}

        <div>
          <Button variant="outline" size="sm" onClick={() => setShowLink((v) => !v)}>
            {showLink
              ? t("stripeCustomer.link.hide")
              : customerId
                ? t("stripeCustomer.link.change")
                : t("stripeCustomer.link.show")}
          </Button>
          {showLink ? (
            <LinkForm name={name} replacing={Boolean(customerId)} onLinked={() => setShowLink(false)} />
          ) : null}
        </div>

        <VatSection name={name} hasCustomer={Boolean(customerId)} vatCheck={vatCheck} confirmation={lastConfirmation} />

        <PartnerSection name={name} partner={partner} />
      </CardContent>
    </Card>
  );
}

function LinkForm({ name, replacing, onLinked }: { name: string; replacing: boolean; onLinked: () => void }) {
  const { t } = useTranslation("billing");
  const qc = useQueryClient();
  const onError = useErrorToast();
  const idPrefix = React.useId();
  const [value, setValue] = React.useState("");
  const [force, setForce] = React.useState(false);
  const id = value.trim();
  const valid = /^cus_[A-Za-z0-9]{6,}$/.test(id);

  const link = useMutation({
    mutationFn: (body: LinkStripeCustomerRequest) =>
      api.post<LinkStripeCustomerResponse>(
        `/api/admin/instances/${encodeURIComponent(name)}/billing/link-stripe-customer`,
        body
      ),
    onSuccess: (res) => {
      const warnings = (res.warnings ?? []).filter(isWarningCode).map((w) => t(`stripeCustomer.warnings.${w}`));
      if (warnings.length) toast.warning(t("stripeCustomer.link.done"), { description: warnings.join(" ") });
      else toast.success(t("stripeCustomer.link.done"));
      setValue("");
      setForce(false);
      invalidateBilling(qc, name);
      onLinked();
    },
    onError,
  });

  return (
    <form
      className="mt-3 space-y-3 rounded-md border p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid && !link.isPending) {
          link.mutate({ stripeCustomerId: id, ...(replacing && force ? { force: true } : {}) });
        }
      }}
    >
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-cus`}>{t("stripeCustomer.link.label")}</Label>
        <div className="flex gap-2">
          <Input
            id={`${idPrefix}-cus`}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={t("stripeCustomer.link.placeholder")}
            className="max-w-xs font-mono text-xs"
            autoComplete="off"
          />
          <Button type="submit" disabled={!valid || link.isPending}>
            {link.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("stripeCustomer.link.submit")}
          </Button>
        </div>
      </div>
      {replacing ? (
        <label htmlFor={`${idPrefix}-force`} className="flex cursor-pointer items-start gap-2 text-sm">
          <input
            id={`${idPrefix}-force`}
            type="checkbox"
            checked={force}
            onChange={(e) => setForce(e.target.checked)}
            className="mt-1"
          />
          <span>
            <span className="font-medium text-destructive">{t("stripeCustomer.link.force")}</span>
            <span className="block text-muted-foreground">{t("stripeCustomer.link.forceHint")}</span>
          </span>
        </label>
      ) : null}
    </form>
  );
}

const OUTCOME_VARIANT = {
  valid: "success",
  valid_simple: "secondary",
  partial: "secondary",
  valid_special: "secondary",
  mismatch: "destructive",
  invalid: "destructive",
  unavailable: "secondary",
  error: "secondary",
  not_requested: "secondary",
} as const;

function VatSection({
  name,
  hasCustomer,
  vatCheck,
  confirmation,
}: {
  name: string;
  hasCustomer: boolean;
  vatCheck: VatCheck | null;
  confirmation: VatConfirmation | null;
}) {
  const { t } = useTranslation("billing");
  const qc = useQueryClient();
  const onError = useErrorToast();
  const idPrefix = React.useId();
  const [acknowledge, setAcknowledge] = React.useState(false);

  // Two steps: «Проверить» only shows the result; «Применить» writes it. A
  // result flagged for review is applied only with the explicit checkbox
  // (the server refuses it otherwise with VAT_REVIEW_REQUIRED).
  const run = useMutation({
    mutationFn: (apply: boolean) =>
      api.post<VatCheckResponse>(`/api/admin/instances/${encodeURIComponent(name)}/billing/vat-check`, {
        apply,
        ...(apply && acknowledge ? { acknowledgeReview: true } : {}),
      }),
    onSuccess: (res) => {
      const warnings = res.warnings.filter(isWarningCode).map((w) => t(`stripeCustomer.warnings.${w}`));
      const message = t(res.applied ? "stripeCustomer.vat.appliedDone" : "stripeCustomer.vat.done", {
        treatment: t(`stripeCustomer.vat.treatments.${res.tax.treatment}`),
        rate: (res.tax.vatRateBps / 100).toFixed(0),
      });
      if (warnings.length) toast.warning(message, { description: warnings.join(" "), duration: 15000 });
      else toast.success(message);
      setAcknowledge(false);
      invalidateBilling(qc, name);
    },
    onError: (err: unknown) => {
      onError(err);
      // A refused apply still stored the fresh check.
      invalidateBilling(qc, name);
    },
  });
  const canApply = hasCustomer && Boolean(vatCheck) && !vatCheck?.applied;
  const needsAck = Boolean(vatCheck?.tax_review);

  const m = confirmation?.matches ?? { company: null, street: null, zip: null, city: null };
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-medium">{t("stripeCustomer.vat.label")}</h3>
      {vatCheck ? (
        <dl className="space-y-1 text-sm">
          <div className="flex gap-2">
            <dt className="w-28 text-muted-foreground">{t("stripeCustomer.vat.label")}</dt>
            <dd className="flex flex-wrap items-center gap-2">
              <span>
                {t(`stripeCustomer.vat.treatments.${vatCheck.tax_treatment}`)} ·{" "}
                {(vatCheck.vat_rate_bps / 100).toFixed(0)}%
              </span>
              <Badge variant={vatCheck.applied ? "success" : "secondary"}>
                {vatCheck.applied ? t("stripeCustomer.vat.applied") : t("stripeCustomer.vat.notApplied")}
              </Badge>
              {vatCheck.tax_review ? (
                <Badge variant="destructive">
                  {t("stripeCustomer.vat.review")}: {vatCheck.tax_review}
                </Badge>
              ) : null}
              <span className="text-xs text-muted-foreground">
                {formatDateTime(vatCheck.at)} · {vatCheck.by}
              </span>
            </dd>
          </div>
          {vatCheck.customer_vat_id ? (
            <div className="flex gap-2">
              <dt className="w-28 text-muted-foreground">{t("stripeCustomer.vat.vatId")}</dt>
              <dd className="font-mono text-xs">{vatCheck.customer_vat_id}</dd>
            </div>
          ) : null}
          {confirmation ? (
            <div className="flex gap-2">
              <dt className="w-28 text-muted-foreground">{t("stripeCustomer.bzst.label")}</dt>
              <dd className="flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={OUTCOME_VARIANT[confirmation.outcome] ?? "secondary"}>
                    {t(`stripeCustomer.bzst.outcomes.${confirmation.outcome}`)}
                  </Badge>
                  <span className="font-mono text-xs">{confirmation.vat_id}</span>
                  <span className="text-xs text-muted-foreground">
                    {formatDateTime(confirmation.at)}
                    {confirmation.status ? ` · ${confirmation.status}` : ""}
                    {confirmation.error ? ` · ${confirmation.error}` : ""}
                  </span>
                </div>
                {confirmation.status === "evatr-0000" ? (
                  <p className="text-xs text-muted-foreground" title={t("stripeCustomer.bzst.matchesHint")}>
                    {t("stripeCustomer.bzst.matches", {
                      company: m.company ?? "—",
                      street: m.street ?? "—",
                      zip: m.zip ?? "—",
                      city: m.city ?? "—",
                    })}
                  </p>
                ) : null}
              </dd>
            </div>
          ) : null}
        </dl>
      ) : (
        <p className="text-sm text-muted-foreground">{t("stripeCustomer.vat.none")}</p>
      )}
      {hasCustomer ? (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={() => run.mutate(false)} disabled={run.isPending}>
              {run.isPending && !run.variables && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("stripeCustomer.vat.check")}
            </Button>
            {canApply ? (
              <Button
                size="sm"
                onClick={() => run.mutate(true)}
                disabled={run.isPending || (needsAck && !acknowledge)}
              >
                {run.isPending && run.variables && <Loader2 className="h-4 w-4 animate-spin" />}
                {t("stripeCustomer.vat.applyButton")}
              </Button>
            ) : null}
          </div>
          {canApply ? (
            <p className="text-xs text-muted-foreground">{t("stripeCustomer.vat.applyHint")}</p>
          ) : null}
          {canApply && needsAck ? (
            <label htmlFor={`${idPrefix}-ack`} className="flex cursor-pointer items-start gap-2 text-sm">
              <input
                id={`${idPrefix}-ack`}
                type="checkbox"
                checked={acknowledge}
                onChange={(e) => setAcknowledge(e.target.checked)}
                className="mt-1"
              />
              <span className="font-medium text-destructive">{t("stripeCustomer.vat.acknowledge")}</span>
            </label>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

const ATTRIBUTION_VARIANT = {
  attributed: "success",
  // Affonso had this email already — possibly as another partner's referral.
  already_exists: "destructive",
  affiliate_not_found: "destructive",
  pending_customer: "secondary",
  no_email: "destructive",
  not_configured: "secondary",
  failed: "destructive",
} as const;

/** Affonso attribution status line (also used by the create dialog). */
export function PartnerStatus({ attribution }: { attribution: PartnerAttribution | null }) {
  const { t } = useTranslation("billing");
  if (!attribution) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <Badge variant={ATTRIBUTION_VARIANT[attribution.status] ?? "secondary"}>
        {t(`stripeCustomer.partner.status.${attribution.status}`)}
      </Badge>
      {attribution.affiliate_name ? <span>{attribution.affiliate_name}</span> : null}
      {attribution.referral_id ? <span className="font-mono">{attribution.referral_id}</span> : null}
      <span className="text-muted-foreground">
        {formatDateTime(attribution.at)}
        {attribution.error ? ` · ${attribution.error}` : ""}
      </span>
    </div>
  );
}

function PartnerSection({ name, partner }: { name: string; partner: InstancePartner | null }) {
  const { t } = useTranslation("billing");
  const qc = useQueryClient();
  const onError = useErrorToast();
  const idPrefix = React.useId();
  const saved = partner?.affonsoReferral ?? "";
  const [value, setValue] = React.useState(saved);
  const status = partner?.attribution?.status;
  // Re-sending the same code retries anything that did not reach Affonso.
  const canRetry = Boolean(saved) && value.trim() === saved && status !== "attributed" && status !== "already_exists";
  React.useEffect(() => setValue(saved), [saved]);

  const save = useMutation({
    mutationFn: (code: string) =>
      api.put<SetPartnerResponse>(`/api/admin/instances/${encodeURIComponent(name)}/billing/partner`, {
        affonsoReferral: code,
      }),
    onSuccess: (res) => {
      toast.success(res.affonsoReferral ? t("stripeCustomer.partner.saved") : t("stripeCustomer.partner.cleared"));
      invalidateBilling(qc, name);
    },
    onError,
  });

  return (
    <section className="space-y-1.5">
      <Label htmlFor={`${idPrefix}-partner`}>{t("stripeCustomer.partner.label")}</Label>
      <div className="flex gap-2">
        <Input
          id={`${idPrefix}-partner`}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={t("stripeCustomer.partner.placeholder")}
          className="max-w-xs"
          maxLength={100}
          autoComplete="off"
        />
        <Button
          variant="secondary"
          onClick={() => save.mutate(value.trim())}
          disabled={save.isPending || value.trim() === saved}
        >
          {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {t("stripeCustomer.partner.save")}
        </Button>
        {canRetry ? (
          <Button variant="outline" onClick={() => save.mutate(saved)} disabled={save.isPending}>
            {t("stripeCustomer.partner.retry")}
          </Button>
        ) : null}
      </div>
      <p className="text-xs text-muted-foreground">{t("stripeCustomer.partner.hint")}</p>
      <PartnerStatus attribution={partner?.attribution ?? null} />
    </section>
  );
}
